import { lazy, useMemo, type FC } from 'react';
import type {
  HydrogenFcfsRoot,
  HydrogenNetworkPointOrBundleSearchParams,
} from 'src/apis/hydrogen-fcfs/types';
import {
  useHydrogenFcfsRoot,
  useHydrogenFcfsRootLink,
} from 'src/apis/hydrogen-fcfs/use-hydrogen-fcfs-root';
import { useHydrogenNetworkPointsOrBundles } from 'src/apis/hydrogen-fcfs/use-hydrogen-network-points-or-bundles';
import { PageSpinner } from 'src/components/spinner-container';
import { useHydrogenNetworkPointsOverviewPageParams } from 'src/pages/h2/network-points/overview/use-page-params';

const Page = lazy(() => import('./page'));

export const NetworkPointsOverview: FC = () => {
  const root = useHydrogenFcfsRoot();
  if (!root?.response) return <PageSpinner />;

  return <NetworkPointsOverviewWithRoot root={root.response.data} />;
};

export const NetworkPointsOverviewWithRoot: FC<{
  root: HydrogenFcfsRoot;
}> = ({ root }) => {
  const pageParams = useHydrogenNetworkPointsOverviewPageParams(root);
  const url = useHydrogenFcfsRootLink('networkPointOverview');

  const params = useMemo(() => {
    const { ownerOrganisation, ...rest } = pageParams.value;
    return {
      ...rest,
      ownerOrganisationId: ownerOrganisation?.value,
    } satisfies HydrogenNetworkPointOrBundleSearchParams;
  }, [pageParams.value]);

  const networkPointsOrBundles = useHydrogenNetworkPointsOrBundles({
    url,
    params,
  });

  if (!networkPointsOrBundles.response) return <PageSpinner />;

  return (
    <Page
      pageParams={pageParams}
      root={root}
      networkPointsOrBundles={networkPointsOrBundles}
    />
  );
};
